import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { useToast } from '@/hooks/useToast';
import { useLanguage } from '@/contexts/LanguageContext';
import { authAPI, warehousesAPI } from '@/lib/api';
import { Building2, User, Mail, Phone, MapPin, Warehouse, ShieldCheck, AlertCircle } from 'lucide-react';
import { countries, phoneCountryCodes, getStatesForCountry, getCitiesForState } from '@/data/countries';

interface RegistrationForm {
  fullName: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  destinationWarehouse: string;
  username: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

export default function Register() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState<RegistrationForm>({
    fullName: '',
    email: '',
    phoneCountryCode: '+218', // Default to Libya
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    destinationWarehouse: '',
    username: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  
  // Track available states and cities based on selections
  const [availableStates, setAvailableStates] = useState<any[]>([]);
  const [availableCities, setAvailableCities] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Email verification state
  const [verificationStep, setVerificationStep] = useState<'form' | 'verify'>('form');
  const [verificationCode, setVerificationCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [codeExpiry, setCodeExpiry] = useState<number>(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  // Load warehouses from public API (no auth required)
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        // Use public endpoint (no authentication required for registration)
        const response = await warehousesAPI.getPublic();
        if (response.success && response.data) {
          setWarehouses(response.data);
        }
      } catch (error) {
        console.error('Error loading warehouses from API:', error);
        // Fallback to localStorage (for development)
        const savedWarehouses = localStorage.getItem('shiphub-warehouses');
        if (savedWarehouses) {
          try {
            const parsedWarehouses = JSON.parse(savedWarehouses);
            setWarehouses(parsedWarehouses);
          } catch (e) {
            console.error('Error loading warehouses:', e);
          }
        }
      }
    };

    loadWarehouses();
  }, []);

  const generateCustomerCode = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const isCustomerCodeUnique = (code: string, existingCustomers: any[]) => {
    return !existingCustomers.some(customer => customer.code === code);
  };

  const isUsernameUnique = (username: string, existingUsers: any[]) => {
    return !existingUsers.some(user => user.username === username);
  };

  const isEmailUnique = (email: string, existingCustomers: any[]) => {
    return !existingCustomers.some(customer => customer.email?.toLowerCase() === email.toLowerCase());
  };

  const handleInputChange = (field: keyof RegistrationForm, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    
    // When country changes, update states and reset state/city
    if (field === 'country' && typeof value === 'string') {
      const states = getStatesForCountry(value);
      setAvailableStates(states);
      setAvailableCities([]);
      setFormData(prev => ({ ...prev, state: '', city: '' }));
    }
    
    // When state changes, update cities and reset city
    if (field === 'state' && typeof value === 'string') {
      const cities = getCitiesForState(formData.country, value);
      setAvailableCities(cities);
      setFormData(prev => ({ ...prev, city: '' }));
    }
  };

  // Generate 6-digit verification code
  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Send verification email
  const sendVerificationEmail = async () => {
    setIsSendingCode(true);
    setError('');

    try {
      // FIRST: Check if email already exists
      const checkResponse = await fetch('https://sourcemygoods.com/api/public/check-email-exists.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const checkResult = await checkResponse.json();

      if (checkResult.exists) {
        setError(t('emailAlreadyRegistered'));
        setIsSendingCode(false);
        return;
      }

      // Email is unique, proceed with sending verification code
      const code = generateVerificationCode();
      const expiryTime = Date.now() + (10 * 60 * 1000); // 10 minutes from now

      // Send email via PHP script
      const response = await fetch('https://sourcemygoods.com/api/public/send-verification-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: formData.email,
          subject: 'SourceMyGoods - Email Verification Code',
          code: code,
          name: formData.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        setSentCode(code);
        setCodeExpiry(expiryTime);
        setVerificationStep('verify');
        
        toast({
          title: t('verificationCodeSent'),
          description: `${t('sixDigitCodeSentTo')} ${formData.email}`,
          duration: 5000,
        });
      } else {
        throw new Error(result.error || 'Failed to send verification email');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email. Please try again.');
      console.error('Email send error:', err);
    } finally {
      setIsSendingCode(false);
    }
  };

  // Verify the code entered by user
  const verifyCode = () => {
    if (verificationCode !== sentCode) {
      setError(t('invalidVerificationCode'));
      return false;
    }

    if (Date.now() > codeExpiry) {
      setError(t('verificationCodeExpired'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate terms acceptance
    if (!formData.acceptTerms) {
      setError(t('mustAcceptTerms'));
      setIsLoading(false);
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordsDoNotMatchError'));
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError(t('passwordTooShort'));
      setIsLoading(false);
      return;
    }

    // If on form step, send verification email
    if (verificationStep === 'form') {
      setIsLoading(false);
      await sendVerificationEmail();
      return;
    }

    // If on verify step, validate the code
    if (verificationStep === 'verify') {
      setIsVerifying(true);
      if (!verifyCode()) {
        setIsLoading(false);
        setIsVerifying(false);
        return;
      }
      setIsVerifying(false);
    }

    try {
      // Call API register endpoint
      const response = await authAPI.register({
        username: formData.username,
        password: formData.password,
        customer_name: formData.fullName,
        email: formData.email,
        phone: `${formData.phoneCountryCode} ${formData.phone}`, // Combine country code with phone
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        destination_warehouse: formData.destinationWarehouse,
      });

      if (response.success && response.data) {
        const customerCode = response.data.user.customer_code;

        // Show success message
        toast({
          title: t('accountCreated'),
          description: t('accountCreatedDesc', { code: customerCode }),
          duration: 5000,
        });

        // Redirect to login
        navigate('/login');
      } else {
        setError(t('registrationFailed'));
      }

    } catch (err: any) {
      setError(err.message || t('registrationFailed'));
      console.error('Registration error:', err);
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicHeader />
      
      <div className="flex-1 bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Registration Card */}
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {verificationStep === 'form' ? t('createAccount') : t('verifyEmail')}
              </CardTitle>
            <CardDescription>
              {verificationStep === 'form' 
                ? t('registerForSourceMyGoods')
                : `${t('enterVerificationCode')} ${formData.email}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Verification Step */}
              {verificationStep === 'verify' && (
                <div className="space-y-4">
                   <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                     <ShieldCheck className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                     <p className="text-sm text-blue-900 font-medium mb-1">
                       {t('checkEmailInbox')}
                     </p>
                     <p className="text-xs text-blue-700">
                       {t('sentSixDigitCode')} {formData.email}
                     </p>
                   </div>

                   {/* Spam Folder Reminder */}
                   <Alert className="bg-yellow-50 border-yellow-200">
                     <AlertCircle className="h-4 w-4 text-yellow-600" />
                     <AlertDescription className="text-yellow-800">
                       <strong>{t('checkSpamFolder')}</strong> {t('spamFolderHint')}
                     </AlertDescription>
                   </Alert>

                   <div className="space-y-2">
                     <Label htmlFor="verificationCode">{t('verificationCodeLabel')} *</Label>
                     <Input
                       id="verificationCode"
                       type="text"
                       value={verificationCode}
                       onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       placeholder={t('enterSixDigitCode')}
                       maxLength={6}
                       className="text-center text-2xl font-mono tracking-widest"
                       required
                     />
                     <p className="text-xs text-gray-500 text-center">
                       {t('codeSentTo')} {formData.email}
                     </p>
                   </div>

                   <div className="flex gap-2">
                     <Button
                       type="button"
                       variant="outline"
                       className="flex-1"
                       onClick={() => {
                         setVerificationStep('form');
                         setVerificationCode('');
                         setError('');
                       }}
                     >
                       {t('back')}
                     </Button>
                     <Button
                       type="button"
                       variant="outline"
                       className="flex-1"
                       onClick={sendVerificationEmail}
                       disabled={isSendingCode}
                     >
                       {isSendingCode ? t('sending') : t('resendCode')}
                     </Button>
                   </div>
                </div>
              )}

              {/* Registration Form */}
              {verificationStep === 'form' && (
                <>
                  {/* Personal Information */}
                  <div className="space-y-3">
                 <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                   <User className="h-4 w-4" />
                   <span>{t('personalInformationSection')}</span>
                 </div>

                <div className="space-y-2">
                  <Label htmlFor="fullName">{t('fullName')} *</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    placeholder={t('fullNamePlaceholder')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phoneNumber')} *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.phoneCountryCode}
                      onValueChange={(value) => handleInputChange('phoneCountryCode', value)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {phoneCountryCodes.map((pc) => (
                          <SelectItem key={pc.code} value={pc.dialCode}>
                            <span className="flex items-center gap-2">
                              <span>{pc.flag}</span>
                              <span>{pc.dialCode}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="912345678"
                      required
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('phoneNumberHint')}
                  </p>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                   <MapPin className="h-4 w-4" />
                   <span>{t('addressInformation')}</span>
                 </div>

                <div className="space-y-2">
                  <Label htmlFor="address">{t('streetAddress')} *</Label>
                  <Input
                    id="address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={t('streetAddressPlaceholder')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">{t('country')} *</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => handleInputChange('country', value)}
                    required
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder={t('selectCountry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                     <Label htmlFor="state">{t('stateProvince')}</Label>
                     {availableStates.length > 0 ? (
                       <Select
                         value={formData.state}
                         onValueChange={(value) => handleInputChange('state', value)}
                         disabled={!formData.country}
                       >
                         <SelectTrigger id="state">
                           <SelectValue placeholder={formData.country ? t('selectState') : t('selectCountryFirst')} />
                         </SelectTrigger>
                         <SelectContent>
                           {availableStates.map((state, index) => (
                             <SelectItem key={index} value={state.name}>
                               {state.name}
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     ) : (
                       <Input
                         id="state"
                         type="text"
                         value={formData.state}
                         onChange={(e) => handleInputChange('state', e.target.value)}
                         placeholder={t('enterState')}
                       />
                     )}
                  </div>

                  <div className="space-y-2">
                     <Label htmlFor="city">{t('city')}</Label>
                     {availableCities.length > 0 ? (
                       <Select
                         value={formData.city}
                         onValueChange={(value) => handleInputChange('city', value)}
                         disabled={!formData.state}
                       >
                         <SelectTrigger id="city">
                           <SelectValue placeholder={formData.state ? t('selectCity') : t('selectStateFirst')} />
                         </SelectTrigger>
                         <SelectContent>
                           {availableCities.map((city, index) => (
                             <SelectItem key={index} value={city.name}>
                               {city.name}
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     ) : (
                       <Input
                         id="city"
                         type="text"
                         value={formData.city}
                         onChange={(e) => handleInputChange('city', e.target.value)}
                         placeholder={t('enterCity')}
                       />
                     )}
                  </div>
                </div>
              </div>

              {/* Destination Warehouse */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                   <Warehouse className="h-4 w-4" />
                   <span>{t('shippingPreferences')}</span>
                 </div>

                <div className="space-y-2">
                  <Label htmlFor="destinationWarehouse">{t('preferredDestinationWarehouse')} *</Label>
                  <Select
                    value={formData.destinationWarehouse}
                    onValueChange={(value) => handleInputChange('destinationWarehouse', value)}
                    required
                  >
                    <SelectTrigger id="destinationWarehouse">
                      <SelectValue placeholder={t('selectWarehouse')} />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.length === 0 ? (
                        <SelectItem value="none" disabled>{t('noWarehousesAvailable')}</SelectItem>
                      ) : (
                        warehouses
                          .filter(warehouse => warehouse.type !== 'Main')
                          .map((warehouse, index) => (
                            <SelectItem key={warehouse.code || warehouse.id || index} value={warehouse.code}>
                              {warehouse.name} - {warehouse.city}, {warehouse.country}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {t('defaultWarehouseHint')}
                  </p>
                </div>
              </div>

              {/* Account Information */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                   <Mail className="h-4 w-4" />
                   <span>{t('accountInformationSection')}</span>
                 </div>

                <div className="space-y-2">
                  <Label htmlFor="username">{t('username')} *</Label>
                  <Input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder={t('chooseUsername')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')} *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={t('createPassword')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('confirmPassword')} *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder={t('confirmPasswordPlaceholder')}
                    required
                  />
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="acceptTerms"
                    checked={formData.acceptTerms}
                    onCheckedChange={(checked) => handleInputChange('acceptTerms', checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                     <Label 
                       htmlFor="acceptTerms" 
                       className="text-sm text-gray-700 cursor-pointer leading-relaxed"
                     >
                       {t('iAcceptThe')}{' '}
                       <Link 
                         to="/terms" 
                         target="_blank"
                         className="text-blue-600 hover:text-blue-800 font-medium underline"
                       >
                         {t('termsAndConditions')}
                       </Link>{' '}
                       {t('andAcknowledge')} *
                     </Label>
                  </div>
                </div>
              </div>
              </>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isSendingCode || isVerifying || (verificationStep === 'form' && !formData.acceptTerms)}
              >
                {verificationStep === 'form' 
                   ? (isSendingCode ? t('sendingCode') : t('continueToVerification'))
                   : (isLoading ? t('creatingAccount') : t('verifyAndCreateAccount'))
                 }
              </Button>
            </form>

          </CardContent>
        </Card>

        {/* Additional Links */}
          <div className="text-center text-sm text-gray-600">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">
              {t('signInHere')}
            </Link>
          </div>
        </div>
      </div>
      
      <PublicFooter />
    </div>
  );
}